import { useLayoutEffect, useEffect, useState } from "react";
import "../../assets/css/Driver.css";
import { FaTruck, FaChevronRight, FaSortAmountUpAlt, FaInfoCircle, FaTrashAlt, FaPlus, FaSearch, FaEuroSign } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { transportsAPI } from '../../api/apiClient';
import PaginationComponent from '../common/PaginationComponent';
import { Spinner, Alert, Button, Modal, Badge, Form, InputGroup, Col, Row } from "react-bootstrap";
import Swal from 'sweetalert2';
import { useSearchFilter } from '../useSearchFilter';

const Transport = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("active");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const filteredData = useSearchFilter(data, searchTerm, ['booking_id']);
  const [counts, setCounts] = useState({
    all: 0,
    car: 0,
    furniture: 0
  });
  
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const [highlightedBookingId, setHighlightedBookingId] = useState(null);

  // Fetch special transports based on tab
  const fetchTransports = async (page = 1, search = '', tab = activeTab) => {
    try {
      setLoading(true);
      setError('');
      
      let response;
      const params = {
        page,
        per_page: pagination.per_page,
        search,
      };

      switch (tab) {
        case "car":
          response = await transportsAPI.cars(params);
          break;
        case "furniture":
          response = await transportsAPI.furnitures(params);
          break;
        case "completed":
          response = await transportsAPI.completed(params);
          break;
        default:
          response = await transportsAPI.index(params);
      }

      if (response.success) {
        const filteredData = response.data.data || response.data;
        setData(filteredData);

        if (response.data.counts) {
          setCounts({
            all: response.data.counts.all || 0,
            car: response.data.counts.car || 0,
            furniture: response.data.counts.furniture || 0,
            completed: response.data.counts.completed || 0
          });
        }

        setPagination(response.data.meta || {
          current_page: page,
          last_page: 1,
          per_page: 10,
          total: response.data.data?.length || 0,
        });
      } else {
        setError(response.message || t('failed_to_fetch_transports'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_transports'));
      console.error('Error fetching transports:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch counts for all tabs
  const fetchAllCounts = async () => {
    try {
      const response = await transportsAPI.index({ per_page: 1 });
      if (response.success && response.data.counts) {
        setCounts(response.data.counts);
      }
    } catch (err) {
      console.error('Error fetching counts:', err);
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      setUpdating(true);
      const response = await transportsAPI.markPaid(id);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.marked_paid_successfully"),
          timer: 3000,
          showConfirmButton: false,
        });
        fetchTransports();
        fetchAllCounts();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_mark_paid"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    const initialTab = location.state?.activeTab || activeTab;
    const highlightId = location.state?.highlightId;
    const scrollToHighlight = location.state?.scrollToHighlight;
    
    setActiveTab(initialTab);
    setHighlightedBookingId(highlightId);
    
    fetchTransports(1, searchTerm, initialTab);
    fetchAllCounts();

    if (location.state?.activeTab) {
      navigate(location.pathname, { replace: true });
    }
  }, [activeTab]);

  // إضافة useEffect للتمرير إلى الـ booking المميز
  useEffect(() => {
    if (highlightedBookingId && location.state?.scrollToHighlight) {
      const element = document.getElementById(`booking-${highlightedBookingId}`);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 500);
      }
    }
  }, [data, highlightedBookingId, location.state]);

  useEffect(() => {
    // استعادة الـ state من الـ location إذا كان موجوداً
    const initialTab = location.state?.activeTab || "active";
    const initialPage = location.state?.currentPage || 1;
    const highlightId = location.state?.highlightId;
    const scrollToHighlight = location.state?.scrollToHighlight;
    
    setActiveTab(initialTab);
    setHighlightedBookingId(highlightId);
    
    fetchTransports(initialPage, searchTerm, initialTab);
    fetchAllCounts();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTransports(1, searchTerm);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchTerm('');
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      const response = await transportsAPI.delete(deleteId);
      
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('transport_deleted_successfully'),
          timer: 3000,
          showConfirmButton: false
        });
        fetchTransports(pagination.current_page);
        fetchAllCounts();
      } else {
        setError(response.message || t('failed_to_delete_transport'));
      }
    } catch (err) {
      setError(t('failed_to_delete_transport'));
      console.error('Error deleting transport:', err);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleViewDetails = (id) => {
    navigate(`/admin/transports/details/${id}`, {
      state: {
        fromPage: pagination.current_page,
        activeTab: activeTab,
        highlightId: id
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProgressPercentage = (transport) => {
    if (!transport) return 0;
    
    const statusOrder = ['pending', 'approved', 'assigned', 'received', 'completed'];
    const currentStatusIndex = statusOrder.indexOf(transport.status);
    return Math.round((currentStatusIndex / (statusOrder.length - 1)) * 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage < 40) return "#e74c3c";
    if (percentage < 80) return "#f39c12";
    return "#2ecc71";
  };

  // const getStatusBadge = (status) => {
  //   const statusConfig = {
  //     assigned: { class: 'status-assigned', text: t('assigned') },
  //     received: { class: 'status-received', text: t('received') },
  //     completed: { class: 'status-completed', text: t('completed') }
  //   };
    
  //   const config = statusConfig[status] || { class: 'status-pending', text: status };
  //   return `<span class="status-badge ${config.class}">${config.text}</span>`;
  // };

  const getStatusBadge = (status) => {
      const statusConfig = {
        cancelled: { variant: 'danger', text: t('cancelled') },
        rejected: { variant: 'danger', text: t('rejected') },
        approved: { variant: 'success', text: t('approved') },
        assigned: { variant: 'info', text: t('assigned') },
        received: { variant: 'secondary', text: t('received') },
        completed: { variant: 'success', text: t('completed') },
        pending: { variant: 'warning', text: t('pending') }
      };
      
      const config = statusConfig[status] || { variant: 'light', text: status };
      return <Badge bg={config.variant}>{config.text}</Badge>;
    };

  if (loading && data.length === 0) {
    return (
      <div className="driver-page">
        <div className="text-center py-4">
          <Spinner animation="border" />
          <p className="mt-2">{t('loading')}</p>
        </div>
      </div>
    );
  }

  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <>
      <div className="driver-page">
        <div className="top-bar">
          {/* <Form onSubmit={handleSearch} className="flex-grow-1 me-3">
            <InputGroup>
              <Form.Control
                placeholder={t("search_by_booking_id")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button variant="outline-secondary" type="submit">
                <FaSearch />
              </Button>
            </InputGroup>
          </Form> */}

          <div className="d-flex gap-2">
            {/* <div className="sort-by">
              <label>{t('sort_by')}:</label>
              <span className="sort-status">
                <FaSortAmountUpAlt className="sort-icon" />
                <strong> {t('status_date')}</strong>
              </span>
            </div> */}

            {/* Search Bar */}
            <Row className="mb-3">
              <Col md={12}>
                <InputGroup>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder={t('search_transport')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <Button 
                      variant="outline-secondary" 
                      onClick={clearSearch}
                      title={t("reset_search")}
                    >
                      ✕
                    </Button>
                  )}
                </InputGroup>
              </Col>
            </Row>
          </div>
          
          <div className="tabs">
            <button
              className={activeTab === "active" ? "active" : ""}
              onClick={() => handleTabChange("active")}
            >
              <span className="count-badge">{counts.all}</span>
              {t("all_transports")}
            </button>
            {/* <button
              className={activeTab === "completed" ? "active" : ""}
              onClick={() => handleTabChange("completed")}
            >
              {t("completed_bookings")}
            </button> */}
            <button
              className={activeTab === "car" ? "active" : ""}
              onClick={() => handleTabChange("car")}
            >
              <span className="count-badge">{counts.car}</span>
              {t("car_transports")}
            </button>
            <button
              className={activeTab === "furniture" ? "active" : ""}
              onClick={() => handleTabChange("furniture")}
            >
              <span className="count-badge">{counts.furniture}</span>
              {t("furniture_transports")}
            </button>
            <button
              className={activeTab === "completed" ? "active" : ""}
              onClick={() => handleTabChange("completed")}
            >
              <span className="count-badge">{counts.completed}</span>
              {t("completed_transports")}
            </button>
            {/* <button
              className={activeTab === "pending" ? "active" : ""}
              onClick={() => handleTabChange("pending")}
            >
              {t("pending_bookings")}
            </button> */}
          </div>
          
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <div className="orders">
          {filteredData.length === 0 ? (
            <div className="text-center py-5">
              <h4>{t('no_transports_found')}</h4>
            </div>
          ) : (
            filteredData.map((transport) => (
              <div 
                className={`order-card ${transport.id === highlightedBookingId ? "booking-highlight" : ""}`} 
                key={transport.id}
                id={`booking-${transport.id}`}
              >
                <div className="order-header d-flex justify-content-between align-items-center flex-wrap">
                  {/* <span className="order-id"><strong>{transport.transport_id}</strong></span> */}
                  <span className="order-id">
                    <strong>
                      {t("Auftrag Nr.")}:
                      {searchTerm ? (
                        <span 
                          dangerouslySetInnerHTML={{
                            __html: transport.booking_id?.replace(
                              new RegExp(searchTerm, 'gi'), 
                              match => `<mark class="bg-warning">${match}</mark>`
                            )
                          }} 
                        />
                      ) : (
                        transport.booking_id
                      )}
                    </strong>
                  </span>

                  <span className="driver">
                    <strong>{t("user")}:</strong> 
                    {transport.user.user_role === "User" ? (
                      <Button
                        variant="link"
                        className="p-0 ms-1 text-decoration-none"
                        onClick={() => navigate(`/admin/customers/details/${transport.user?.id}`)}
                      >
                        {transport.user?.user_name || t('not_assigned')}
                      </Button>
                    ) : (
                      <span className='ms-1'>
                        {transport.user?.user_name || t('not_assigned')}
                      </span>
                    )}
                  </span>

                  <span className="created">{transport.created_at}</span>

                  <span className="price d-flex justify-content-end flex-wrap">
                    {getStatusBadge(transport.status || 'pending')}

                    <Badge bg={transport.payment_status ? 'success' : 'warning'} className="ms-2">
                      {t(transport.payment) || t('unpaid')}
                    </Badge>

                    {transport.transport_type == "car" ? (
                      <Badge bg={'success'} className="ms-2">
                        {t('car')}
                      </Badge>
                    ) : (
                      <Badge bg={'success'} className="ms-2">
                        {t('furniture')}
                      </Badge>
                    )}
                  </span>
                </div>

                <div className="order-body">
                  <div className="left d-flex justify-content-between flex-wrap">
                    <div className="d-flex flex-column">
                      <strong>{t("booking.transport_type")}:</strong> 
                      {t(transport.transport_type) || t('not_available')}
                    </div>
                    <div className="d-flex flex-column">
                      <strong>{t("booking.item_type")}:</strong> 
                      {transport.item_type?.[0] || t('not_available')} 
                    </div>
                    <div className="d-flex flex-column">
                      {Array.isArray(transport.num_items) && transport.num_items.length > 0 && transport.num_items[0] !== '' ? (
                        <>
                        <strong>{t("booking.num_items")}:</strong> {transport.num_items[0]} {t('items')}
                        </>
                      ) : null}
                    </div>


                    <div className="delivery-path-vertical">
                      <div className="circle-label-vertical">
                        <span className="circle"></span>
                        <span className="label">{transport.booking_from}</span>
                      </div>
                      <div className="line-container">
                        <div className="line-vertical"></div>
                      </div>
                      <div className="circle-label-vertical">
                        <span className="circle"></span>
                        <span className="label">{transport.booking_to}</span>
                      </div>
                    </div>
                  </div>

                  <div className="right d-flex justify-content-between">
                    <div className="progress-bar-container mt-2">
                      <div className="progress-header d-flex justify-content-between align-items-center mb-1">
                        <strong>{t("progress")}:</strong>
                        <span className="progress-label">{getProgressPercentage(transport)}%</span>
                      </div>
                      
                      <div className="progress-bar-outer">
                        <div
                          className="progress-bar-inner"
                          style={{
                            width: `${getProgressPercentage(transport)}%`,
                            background: getProgressColor(getProgressPercentage(transport)),
                          }}
                        ></div>
                      </div>
                    </div>
                    {transport.dimensions && (
                      <span className="price">
                        <strong>{t("booking.dimensions")}:</strong> {transport.dimensions?.[0] || t('not_available')}
                      </span>
                      )
                    }
                    <span className="price"></span>
                    <span className="price"> </span>
                  </div>
                </div>

                <div className="order-footer">
                  {/* <div>
                    <strong>
                      {booking.order?.status === 'assigned' ? t("assigned_on") : 
                      booking.order?.status === 'received' ? t("received_on") : 
                      t("completed_on")}
                    </strong>{" "}
                    {formatDate(booking.order?.assigned_date || booking.created_at)}
                  </div> */}
                  <div>
                    <strong>
                      {t("booking.loading_date")}:
                    </strong>{" "}
                    {transport.loading_date || transport.created_at}
                  </div>
                  <div>
                    <strong>{t("distance")}:</strong> {transport.distance} | 
                    <strong> {t("duration")}:</strong> {transport.total_duration}
                  </div>
                  <div>
                    <strong>{t("total_weight")}:</strong> {transport.total_weight} Kg
                  </div>

                  <div className="action-buttons">
                    {transport.payment_status === 0 && (
                      <Button 
                        size="sm"
                        variant={transport.payment_status ? "success" : "outline-success"}
                        onClick={() => handleMarkPaid(transport.id)}
                        disabled={updating || transport.payment_status}
                      >
                        <FaEuroSign /> {t("booking.mark_paid")}
                      </Button>
                    )}
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => handleViewDetails(transport.id)}
                      className="mx-2"
                    >
                      <FaInfoCircle /> {t("details")}
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteClick(transport.id)}
                    >
                      <FaTrashAlt /> {t("delete")}
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {pagination.total > 0 && (
          <PaginationComponent
            currentPage={pagination.current_page}
            lastPage={pagination.last_page}
            onPageChange={fetchTransports}
            disabled={loading}
          />
        )}

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t('confirm_deletion')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t('confirm_delete_transport')}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleDeleteCancel}>
              {t('cancel')}
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm} disabled={loading}>
              {loading ? t('deleting') : t('delete')}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      <style>{`
        .booking-highlight {
          background-color: #d4edda !important;
          border-left: 4px solid #28a745;
          transition: all 0.3s ease;
          box-shadow: 0 0 10px rgba(40, 167, 69, 0.3);
        }
        
        .booking-highlight .order-header,
        .booking-highlight .order-body,
        .booking-highlight .order-footer {
          background-color: transparent;
        }
        
        .booking-highlight .order-id strong {
          color: #155724;
          font-size: 1.1em;
        }
      `}</style>
    </>
  );
};

export default Transport;